import { useMemo } from "react";

import type { AreaGraphProps } from "@similarweb/pro-logic-components";

import { DateFormatter } from "app/pages/digital-marketing/common/services/formatters/dateFormatter";
import { CompetitorsChartTooltip } from "app/pages/digital-marketing/monitor-and-protect/shared/components/competitors-chart/competitor-chart-tooltip";
import type {
    GranularityType,
    ValueType,
} from "app/pages/digital-marketing/monitor-and-protect/shared/components/competitors-chart/types";

import { competitorsChartConfigBuilder, getXAxisFormatter, getYAxisFormatter } from "./chart-assets";

export const useCompetitorsChartConfig = (
    granularity: GranularityType,
    valueType: ValueType,
    chartData?: AreaGraphProps<unknown>["data"],
) => {
    const derivedState = useMemo(() => {
        const allXValues: number[] = [];
        if (chartData && chartData.length > 0) {
            chartData.forEach((series) => {
                if (series.data && series.data.length > 0) {
                    series.data.forEach((point) => {
                        // @ts-expect-error types in pro-logic
                        if (point.x !== undefined && point?.x !== null && !allXValues.includes(point.x)) {
                            // @ts-expect-error types in pro-logic
                            allXValues.push(point.x);
                        }
                    });
                }
            });
        }
        allXValues.sort((a, b) => a - b);

        return {
            yAxisFormatter: getYAxisFormatter(valueType),
            xAxisFormatter: getXAxisFormatter(granularity),
            xValues: allXValues,
        };
    }, [valueType, granularity, chartData]);

    return useMemo(
        () =>
            competitorsChartConfigBuilder
                .withYAxisLabelFormatter(derivedState.yAxisFormatter)
                .withYAxisConfig({
                    tickAmount: 5,
                    allowDecimals: true,
                    minorTickInterval: null,
                })
                .withXAxisLabelFormatter(derivedState.xAxisFormatter)
                .withXAxisConfig({
                    showFirstLabel: true,
                    showLastLabel: true,
                    startOnTick: true,
                    endOnTick: true,
                    tickPositioner: function () {
                        const xValues = derivedState.xValues;

                        if (!xValues || xValues.length === 0) {
                            return [];
                        }

                        // Если мало значений, показываем все
                        if (xValues.length <= 8) {
                            return xValues;
                        }

                        // Calculate optimal tick count based on chart width
                        const chartWidth = this.chart?.chartWidth || 800;
                        const targetCount = Math.min(Math.max(Math.floor(chartWidth / 120), 4), 8);

                        const positions: number[] = [];

                        // Always include first value
                        positions.push(xValues[0]);

                        if (targetCount > 2 && xValues.length > 2) {
                            // Evenly distribute intermediate ticks
                            const step = (xValues.length - 1) / (targetCount - 1);

                            for (let i = 1; i < targetCount - 1; i++) {
                                const index = Math.round(step * i);
                                // Ensure index is within bounds and not duplicated
                                if (index > 0 && index < xValues.length && !positions.includes(xValues[index])) {
                                    positions.push(xValues[index]);
                                }
                            }
                        }

                        if (xValues.length > 1 && !positions.includes(xValues[xValues.length - 1])) {
                            positions.push(xValues[xValues.length - 1]);
                        }

                        return positions.sort((a, b) => a - b);
                    },
                })
                .withPlotOptions({
                    area: {
                        stacking: valueType === "absolute" ? "normal" : "percent",
                        connectNulls: true,
                    },
                })
                .withChartTooltip((points) => {
                    const [first] = points;

                    const period = first.point.options.period
                        ? `From ${DateFormatter.toShortMonthDay(first.point.options.period.from)} to ${DateFormatter.toShortMonthDay(first.point.options.period.to)} `
                        : DateFormatter.toShortMonthDay(first.point.x);

                    const visibleTotal = points.reduce((acc, e) => acc + e.point.y, 0);

                    return (
                        <CompetitorsChartTooltip
                            period={period}
                            competitors={points.map((e) => ({
                                name: e.options.name,
                                color: e.options.color as string,
                                totalClicks: e.point.options.absolute,
                                share: e.point.options.absolute == null ? null : e.point.y / visibleTotal,
                            }))}
                        />
                    );
                })
                .build(),
        [derivedState.yAxisFormatter, derivedState.xAxisFormatter, derivedState.xValues, valueType],
    );
};
